import { isDefined } from '@sgme/fp';
import { YesNoRadioButtons } from 'components/shared/ButtonsGroup/YesNoRadioButtons';
import { NumberFormatter } from 'components/shared/NumberFormatter/NumberFormatter';
import { ChangeEvent, FC, useCallback, useState } from 'react';
import { Correlation } from 'state/quotes/correlations/correlations.model';
import {
  interpolateCorrelationFromPrice,
  interpolatePriceFromCorrelation,
} from 'utils/interpolation/interpolatePriceNext';

import { Table } from '../Graphs.model';

export interface CorrelationInputsProps {
  tabIndex?: number;
  priceTable?: Table;
  correlation?: Correlation;
  useOppositeInterpolation?: boolean;
  onChanged: (correlation: Omit<Correlation, 'saveCorrelation'>) => void;
}

type Focus = 'bidPrice' | 'askPrice' | 'bidCorrelation' | 'askCorrelation';

const defaultCorrelation = {
  bidPrice: undefined,
  askPrice: undefined,
  bidCorrelation: undefined,
  askCorrelation: undefined,
};

export const CorrelationInputs: FC<CorrelationInputsProps> = ({
  tabIndex,
  priceTable = { columns: [], rows: [[]] },
  correlation,
  useOppositeInterpolation = false,
  onChanged,
}) => {
  const { bidPrice, askPrice, bidCorrelation, askCorrelation } = correlation ?? defaultCorrelation;

  const [focus, setFocus] = useState<Focus>();

  const shouldShowExtraOptions = isDefined(correlation?.pickingFrequency) && isDefined(correlation.isAsynchronous);

  const handleBidPriceChanged = useCallback(
    (value?: Nullable<number>) => {
      if (focus === 'bidPrice') {
        const interpolated = value === null ? null : interpolateCorrelationFromPrice(priceTable, value);
        const interpolatedCorrelation = useOppositeInterpolation
          ? { askCorrelation: interpolated }
          : { bidCorrelation: interpolated };
        onChanged({
          ...correlation,
          bidPrice: value,
          ...interpolatedCorrelation,
        });
      }
    },
    [focus, onChanged, correlation, priceTable, useOppositeInterpolation],
  );

  const handleAskPriceChanged = useCallback(
    (value?: Nullable<number>) => {
      if (focus === 'askPrice') {
        const interpolated = value === null ? null : interpolateCorrelationFromPrice(priceTable, value);
        const interpolatedCorrelation = useOppositeInterpolation
          ? { bidCorrelation: interpolated }
          : { askCorrelation: interpolated };

        onChanged({
          ...correlation,
          askPrice: value,
          ...interpolatedCorrelation,
        });
      }
    },
    [focus, onChanged, correlation, priceTable, useOppositeInterpolation],
  );

  const handleBidCorrelationChange = useCallback(
    (value?: Nullable<number>) => {
      if (focus === 'bidCorrelation') {
        const interpolated = value === null ? null : interpolatePriceFromCorrelation(priceTable, value);
        const interpolatedPrice = useOppositeInterpolation ? { askPrice: interpolated } : { bidPrice: interpolated };
        onChanged({
          ...correlation,
          bidCorrelation: value,
          ...interpolatedPrice,
        });
      }
    },
    [focus, onChanged, correlation, priceTable, useOppositeInterpolation],
  );

  const handleAskCorrelationChange = useCallback(
    (value?: Nullable<number>) => {
      if (focus === 'askCorrelation') {
        const interpolated = value === null ? null : interpolatePriceFromCorrelation(priceTable, value);
        const interpolatedPrice = useOppositeInterpolation ? { bidPrice: interpolated } : { askPrice: interpolated };
        onChanged({
          ...correlation,
          askCorrelation: value,
          ...interpolatedPrice,
        });
      }
    },
    [focus, onChanged, correlation, priceTable, useOppositeInterpolation],
  );

  const handlePricingFrequencyChange = useCallback(
    (event?: ChangeEvent<HTMLSelectElement>) => {
      onChanged({
        ...correlation,
        pickingFrequency: Number(event?.target.value),
      });
    },
    [onChanged, correlation],
  );

  const handleAsynchronousAverageChange = useCallback(
    (value: boolean) => {
      onChanged({
        ...correlation,
        isAsynchronous: value,
      });
    },
    [onChanged, correlation],
  );

  return (
    <div className="d-flex flex-column gap-12px">
      <table className="table table-sm my-0, bg-lvl1 table-striped">
        <thead>
          <tr>
            <th scope="col"></th>
            <th scope="col">Bid</th>
            <th scope="col">Ask</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row" className="align-middle">
              Price
            </th>
            <td>
              <NumberFormatter
                className="form-control"
                id="bid-price"
                value={bidPrice}
                onFocus={() => setFocus('bidPrice')}
                onNullableChange={handleBidPriceChanged}
                precision={4}
                tabIndex={tabIndex}
                e2e={'bid-price-input'}
              />
            </td>
            <td>
              <NumberFormatter
                className="form-control"
                id="ask-price"
                value={askPrice}
                onFocus={() => setFocus('askPrice')}
                onNullableChange={handleAskPriceChanged}
                precision={4}
                tabIndex={tabIndex}
                e2e={'ask-price-input'}
              />
            </td>
          </tr>
          <tr>
            <th scope="row" className="align-middle">
              Correlation
            </th>
            <td>
              <NumberFormatter
                className="form-control"
                id="bid-correlation"
                value={bidCorrelation}
                onFocus={() => setFocus('bidCorrelation')}
                onNullableChange={handleBidCorrelationChange}
                precision={4}
                tabIndex={tabIndex}
                e2e={'bid-correlation-input'}
              />
            </td>
            <td>
              <NumberFormatter
                className="form-control"
                id="ask-correlation"
                value={askCorrelation}
                onFocus={() => setFocus('askCorrelation')}
                onNullableChange={handleAskCorrelationChange}
                precision={4}
                tabIndex={tabIndex}
                e2e={'ask-correlation-input'}
              />
            </td>
          </tr>
        </tbody>
      </table>
      {shouldShowExtraOptions && (
        <div className="d-flex gap-12px">
          <div className="form-group w-50">
            <label className="form-label mb-4px">Picking frequency</label>
            <select
              className="form-select form-control-md"
              value={correlation?.pickingFrequency}
              onChange={handlePricingFrequencyChange}
            >
              <option value={1}>1</option>
              <option value={3}>3</option>
              <option value={5}>5</option>
            </select>
          </div>
          <div className="w-50">
            <label className="form-label mb-4px">Asynchronous average</label>
            <YesNoRadioButtons
              className="w-100"
              value={correlation?.isAsynchronous ?? null}
              onSelect={handleAsynchronousAverageChange}
            />
          </div>
        </div>
      )}
    </div>
  );
};
